import { useCallback, useMemo, useRef, useState } from "react";
import { ChatDialog, MatchCase } from "@ptsd/uikit";
import { useMedia } from "tamagui";

import type { RouterOutputs } from "@edenlabllc/ptsd-api";

import { api } from "~/utils/api";
import { ChatContentDesktop } from "~/component/feature/chat-bot/chat-content-desktop";
import { ChatContentMobile } from "~/component/feature/chat-bot/chat-content-mobile";

export type ChatMessage =
  RouterOutputs["chat"]["getMessages"]["messages"][number] & {
    isIntro?: boolean;
  };

export type ChatHistoryItem =
  RouterOutputs["chat"]["getChatsList"]["chats"][number];

export interface ChatContentProps {
  chatId?: number;
  onSubmit: (text: string) => void;
  onChangeChat: (chatId: number) => void;
  onCreateNewChat: () => void;
  onCreateFirstChat: () => void;
  isLoading: boolean;
  isChatLoading: boolean;
  isDisabled: boolean;
  text: string;
  onChangeText: (text: string) => void;
  messages: ChatMessage[];
  followups?: string[];
  chatHistory?: ChatHistoryItem[];
}

export const ChatHolder = () => {
  const { sm } = useMedia();
  const [text, onChangeText] = useState("");
  const { open } = ChatDialog.useContext();
  const [{ chats }] = api.chat.getChatsList.useSuspenseQuery();
  const messagesCount = useRef(0);
  const [chatId, setChatId] = useState<number | undefined>(chats[0]?.id);

  const onChangeChat = (chatId: number) => {
    setChatId(chatId);
    messagesCount.current = 0;
  };

  const utils = api.useUtils();

  const getMessagesQuery = api.chat.getMessages.useQuery(
    { chatId: chatId ?? 0 },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: true,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      enabled: open && chatId !== undefined,
    },
  );

  const messages: ChatMessage[] =
    chats.length > 0
      ? getMessagesQuery.data?.messages ?? []
      : [
          {
            mine: false,
            messageText:
              "Вітаю! 👋\n" +
              "Я ваш AI-помічник на платформі діагностики психологічного стану, створений, щоб підтримувати вас на кожному етапі шляху до самопізнання 🌿\n" +
              "Моя мета — забезпечити комфортну та конфіденційну взаємодію з вашими результатами 🔒\n" +
              "Розпочнімо вашу подорож до кращого розуміння себе 💛",
            isIntro: true,
          } as ChatMessage,
        ];

  const onCreateFirstChat = () => {
    createFirstChatMutation.mutate();
  };

  const generateNameAndTagsMutation = api.chat.generateNameAndTags.useMutation({
    onSuccess: (newData) => {
      utils.chat.getChatsList.setData(undefined, (currentData) => {
        if (!currentData) {
          return currentData;
        }
        return {
          ...currentData,
          chats: currentData?.chats?.map((el) => {
            return el.id === newData.id ? newData : el;
          }),
        };
      });
    },
  });

  const createFirstChatMutation = api.chat.createFirstChat.useMutation({
    onSuccess: (newData) => {
      utils.chat.getChatsList.setData(undefined, (currentData) => {
        return {
          chats: [...(currentData?.chats ?? []), newData.chat],
        };
      });
      setChatId(newData.chat.id);
    },
  });

  const sendMessageMutation = api.chat.sendMessage.useMutation({
    onSuccess: (newData) => {
      if (!chatId) {
        return;
      }
      messagesCount.current++;
      void getFollowupsQuery.refetch();
      utils.chat.getMessages.setData({ chatId }, (currentData) => {
        return {
          quick: newData.quick,
          messages: [
            ...(currentData?.messages ?? []),
            ...(newData?.messages ?? []),
          ],
        };
      });
      onChangeText("");
      if (messagesCount.current % 2 === 0) {
        void generateNameAndTagsMutation.mutate({ chatId });
      }
    },
  });

  const { mutate: createChat } = api.chat.createChat.useMutation({
    onSuccess: (newData) => {
      utils.chat.getChatsList.setData(undefined, (currentData) => {
        return {
          chats: [...(currentData?.chats ?? []), newData],
        };
      });
      onChangeText("");
      onChangeChat(newData.id);
    },
  });

  const onCreateNewChat = () => {
    createChat();
  };

  const getFollowupsQuery = api.chat.getFollowup.useQuery(
    {
      chatId: chatId ?? 0,
    },
    {
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryOnMount: false,
      enabled: !!chatId && (getMessagesQuery.data?.messages?.length ?? 3) !== 3,
    },
  );

  const followups = useMemo(() => {
    if (!getMessagesQuery.data) {
      return [];
    }

    if (getMessagesQuery.data?.messages?.length === 3) {
      return [
        "Чи замінює AI-помічник психолога?",
        "Які мої сильні сторони?",
        "Яка моя найкритичніша шкала?",
        "Які вправи можна робити самостійно?",
        "Чи потрібен мені психолог?",
      ];
    }

    if (getFollowupsQuery.isRefetching) {
      return [];
    }

    return getFollowupsQuery.data ?? [];
  }, [
    getFollowupsQuery.data,
    getFollowupsQuery.isRefetching,
    getMessagesQuery.data,
  ]);

  const onSubmit = useCallback(
    (text: string) => {
      if (text.length > 0 && chatId) {
        sendMessageMutation.mutate({ messageText: text, chatId });
      }
    },
    [chatId, sendMessageMutation],
  );

  if (!open) {
    return null;
  }

  return (
    <MatchCase value={sm}>
      {(Case) => (
        <ChatDialog.Content
          style={{
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <Case condition={false}>
            <ChatContentDesktop
              onCreateFirstChat={onCreateFirstChat}
              chatId={chatId}
              onChangeChat={onChangeChat}
              onCreateNewChat={onCreateNewChat}
              messages={messages}
              text={text}
              followups={followups ?? []}
              onChangeText={onChangeText}
              isLoading={
                sendMessageMutation.isLoading ||
                createFirstChatMutation.isLoading
              }
              isChatLoading={getMessagesQuery.isFetching}
              isDisabled={text.length === 0}
              onSubmit={onSubmit}
              chatHistory={chats}
            />
          </Case>
          <Case condition={true}>
            <ChatContentMobile
              onCreateFirstChat={onCreateFirstChat}
              chatId={chatId}
              onChangeChat={onChangeChat}
              onCreateNewChat={onCreateNewChat}
              messages={messages}
              isChatLoading={getMessagesQuery.isFetching}
              followups={followups ?? []}
              text={text}
              onChangeText={onChangeText}
              isLoading={
                sendMessageMutation.isLoading ||
                createFirstChatMutation.isLoading
              }
              isDisabled={text.length === 0}
              onSubmit={onSubmit}
              chatHistory={chats}
            />
          </Case>
        </ChatDialog.Content>
      )}
    </MatchCase>
  );
};
